import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { submitMedicalProviderApplication } from "../firebase/firestore";
import { PDFDocument, rgb } from "pdf-lib";
import "./ContactForm.css";

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        specialty: "",
        practiceName: "",
        dbaName: "",
        location: "",
        referralEmail: "",
        billingContact: "",
        billingEmail: "",
        address: "",
        city: "",
        zip: "",
        crossStreets: "",
        practicePhone: "",
        practiceFax: "",
        urgentAppointments: "",
        availableHours: "",
        message: "",
        npi: "",
        taxId: "",
        licenseNumber: "",
        termsAccepted: false,
        interest: "join",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [signature, setSignature] = useState("");
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // Phone number formatting function
    const formatPhoneNumber = (value) => {
        // Remove all non-numeric characters
        const phoneNumber = value.replace(/\D/g, "");

        // Format based on length
        if (phoneNumber.length === 0) return "";
        if (phoneNumber.length <= 3) return `(${phoneNumber}`;
        if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    // Helper to format specialty for display
    const formatSpecialty = (specialty) => {
        const specialtyMap = {
            'chiropractor': 'Chiropractor',
            'physical-therapy': 'Physical Therapy',
            'pain-management': 'Pain Management',
            'orthopedics': 'Orthopedics',
            'neurology': 'Neurology',
            'imaging': 'Imaging',
            'medical-doctor': 'Medical Doctor',
            'nurse-practitioner': 'Nurse Practitioner',
            'physician-assistant': 'Physician Assistant',
            'acupuncturist': 'Acupuncturist',
            'massage-therapist': 'Massage Therapist',
            'other': 'Other Medical Specialty'
        };
        return specialtyMap[specialty] || specialty || '';
    };

    // Helper to format state for display
    const formatState = (location) => {
        const stateMap = {
            'arizona': 'Arizona',
            'texas': 'Texas',
            'colorado': 'Colorado',
            'nevada': 'Nevada',
            'new-mexico': 'New Mexico',
            'washington': 'Washington'
        };
        return stateMap[location] || location || '';
    };

    // Open filled agreement in new tab for printing/saving as PDF
    const openFilledAgreementForPrint = () => {
        const iframe = document.getElementById('termsIframe');
        if (!iframe || !iframe.contentDocument) {
            alert("Please open the terms modal first.");
            return;
        }

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Clone the filled HTML content
        const htmlContent = iframeDoc.documentElement.outerHTML;

        // Open in new window
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        // Trigger print after a brief delay to ensure content loads
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

// ========== PDF POSITIONING CONFIG - ADJUST THESE VALUES ==========
    // x=0 is left edge, y=0 is BOTTOM of page
    // Letter size: 612 x 792 points
    const PDF_CONFIG = {
        // Which page (from end): 1 = last page, 2 = second to last, etc.
        pageFromEnd: 2,

        // Contractor section positioning (LEFT ALIGNED)
signatureX: 85,
        signatureY: 102,
        signatureWidth: 120,
        signatureHeight: 30,

        businessNameX: 54,
        businessNameY: 123,

        nameX: 95,
        nameY: 91,

        licenseX: 180,
        licenseY: 76,

        dateX: 85,
        dateY: 61,

        fontSize: 10,
    };

    // Generate signed PDF using pdf-lib with x,y positioning
    const generateSignedPdfBlob = async (forDownload = false) => {
        try {
            console.log("Starting PDF generation with pdf-lib...");
            console.log("Config:", PDF_CONFIG);

            // Fetch the original PDF
            const pdfUrl = "/agreements/Stamp_Medical_Clinical_Contractor_Agreement.pdf";
            const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());

            // Load the PDF
            const pdfDoc = await PDFDocument.load(existingPdfBytes);

            // Get the target page (counting from end)
            const pages = pdfDoc.getPages();
            const pageIndex = pages.length - PDF_CONFIG.pageFromEnd;
            const targetPage = pages[pageIndex];
            console.log(`Using page ${pageIndex + 1} of ${pages.length} (${PDF_CONFIG.pageFromEnd} from end)`);

            // Draw signature image
            if (signature && signature.startsWith("data:image")) {
                try {
                    const signatureImageBytes = await fetch(signature).then((res) => res.arrayBuffer());
                    const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

                    targetPage.drawImage(signatureImage, {
                        x: PDF_CONFIG.signatureX,
                        y: PDF_CONFIG.signatureY,
                        width: PDF_CONFIG.signatureWidth,
                        height: PDF_CONFIG.signatureHeight,
                    });
                    console.log("Signature drawn at:", PDF_CONFIG.signatureX, PDF_CONFIG.signatureY);
                } catch (sigError) {
                    console.error("Error embedding signature:", sigError);
                }
            }

            // Draw business name on the By: line (after signature)
            const businessName = formData.dbaName || formData.practiceName;
            if (businessName) {
                targetPage.drawText(businessName, {
                    x: PDF_CONFIG.businessNameX,
                    y: PDF_CONFIG.businessNameY,
                    size: PDF_CONFIG.fontSize,
                    color: rgb(0, 0, 0),
                });
            }

            // Draw name
            if (formData.name) {
                targetPage.drawText(formData.name, {
                    x: PDF_CONFIG.nameX,
                    y: PDF_CONFIG.nameY,
                    size: PDF_CONFIG.fontSize,
                    color: rgb(0, 0, 0),
                });
                console.log("Name drawn at:", PDF_CONFIG.nameX, PDF_CONFIG.nameY);
            }

            // Draw license type / state
            const licenseText = `${formatSpecialty(formData.specialty)} / ${formatState(formData.location)}`;
            targetPage.drawText(licenseText, {
                x: PDF_CONFIG.licenseX,
                y: PDF_CONFIG.licenseY,
                size: PDF_CONFIG.fontSize,
                color: rgb(0, 0, 0),
            });
            console.log("License drawn at:", PDF_CONFIG.licenseX, PDF_CONFIG.licenseY);

            // Draw date
            targetPage.drawText(new Date().toLocaleDateString(), {
                x: PDF_CONFIG.dateX,
                y: PDF_CONFIG.dateY,
                size: PDF_CONFIG.fontSize,
                color: rgb(0, 0, 0),
            });
            console.log("Date drawn at:", PDF_CONFIG.dateX, PDF_CONFIG.dateY);

            // Save the PDF
            const pdfBytes = await pdfDoc.save();
            const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

            console.log("PDF generated, size:", pdfBlob.size);

            // If for download, trigger download
            if (forDownload) {
                const url = URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'Stamp_Medical_Signed_Agreement_TEST.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }

            return pdfBlob;
        } catch (error) {
            console.error("Error generating PDF:", error);
            return null;
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Clear invalid class when user starts typing
        e.target.classList.remove('invalid');

        // Format phone number if it's a phone field
        const formattedValue = (name === "phone" || name === "practicePhone" || name === "practiceFax") ? formatPhoneNumber(value) : value;

        setFormData((prev) => {
            const newData = {
                ...prev,
                [name]: type === "checkbox" ? checked : formattedValue,
            };

            // Auto-populate billing fields when name or email changes
            if (name === "name" && value) {
                newData.billingContact = value;
            }
            if (name === "email" && value) {
                newData.billingEmail = value;
            }

            return newData;
        });
    };

// Clear invalid class from all fields
    const clearInvalidFields = () => {
        document.querySelectorAll('.invalid').forEach(el => el.classList.remove('invalid'));
    };

    // Validation for step 1
    const validateStep1 = () => {
        clearInvalidFields();
        const requiredFields = ['name', 'email', 'phone', 'specialty', 'location'];
        let isValid = true;
        let firstInvalidField = null;

        requiredFields.forEach(field => {
            const element = document.getElementById(field);
            if (!formData[field] || formData[field].trim() === '') {
                if (element) {
                    element.classList.add('invalid');
                    if (!firstInvalidField) firstInvalidField = element;
                }
                isValid = false;
            }
        });

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const emailElement = document.getElementById('email');
        if (formData.email && !emailRegex.test(formData.email)) {
            if (emailElement) {
                emailElement.classList.add('invalid');
                if (!firstInvalidField) firstInvalidField = emailElement;
            }
            isValid = false;
        }

        if (firstInvalidField) {
            firstInvalidField.focus();
        }

        return isValid;
    };

    // Validation for step 2
    const validateStep2 = () => {
        clearInvalidFields();
        const requiredFields = ['address', 'city', 'zip', 'crossStreets', 'practicePhone', 'availableHours', 'urgentAppointments', 'message'];
        let isValid = true;
        let firstInvalidField = null;

        requiredFields.forEach(field => {
            const element = document.getElementById(field);
            if (!formData[field] || formData[field].trim() === '') {
                if (element) {
                    element.classList.add('invalid');
                    if (!firstInvalidField) firstInvalidField = element;
                }
                isValid = false;
            }
        });

        if (firstInvalidField) {
            firstInvalidField.focus();
        }

        return isValid;
    };

    const handleNextStep = () => {
        if (currentStep === 1) {
            if (validateStep1()) {
                setCurrentStep(2);
            }
        } else if (currentStep === 2) {
            if (validateStep2()) {
                setCurrentStep(3);
            }
        }
    };

    const handlePrevStep = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
        } else if (currentStep === 3) {
            setCurrentStep(2);
        }
    };

    const openTermsModal = () => {
        setShowTermsModal(true);
    };

    // Get the terms content
    const getTermsContent = () => {
        return {
            title: "Stamp Medical Clinical Contractor Agreement",
        };
    };

    const closeTermsModal = () => {
        setShowTermsModal(false);
    };

    // Signature canvas functions
    const startDrawing = (e) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (isDrawing) {
            setIsDrawing(false);
            // Save signature as data URL
            const canvas = canvasRef.current;
            const dataURL = canvas.toDataURL();
            setSignature(dataURL);
        }
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignature("");
    };

    // Initialize canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext("2d");
            ctx.strokeStyle = "#000000";
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
        }
    }, [currentStep]);

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === "Escape" && showTermsModal) {
                setShowTermsModal(false);
            }
        };

        document.addEventListener("keydown", handleEscKey);
        return () => document.removeEventListener("keydown", handleEscKey);
    }, [showTermsModal]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

try {
            // Generate the signed PDF blob
            let pdfBlob = null;
            if (signature) {
                console.log("Generating signed PDF...");
                pdfBlob = await generateSignedPdfBlob();
                console.log("PDF blob result:", pdfBlob ? `Generated (${pdfBlob.size} bytes)` : "FAILED - null");
            } else {
                console.log("No signature, skipping PDF generation");
            }

            console.log("Submitting to Firestore with PDF blob:", !!pdfBlob);
            await submitMedicalProviderApplication({
                ...formData,
                signature: signature,
            }, pdfBlob);

            setSubmitStatus("success");
            setFormData({
                name: "",
                email: "",
                phone: "",
                specialty: "",
                practiceName: "",
                dbaName: "",
                location: "",
                referralEmail: "",
                billingContact: "",
                billingEmail: "",
                address: "",
                city: "",
                zip: "",
                crossStreets: "",
                practicePhone: "",
                practiceFax: "",
                urgentAppointments: "",
                availableHours: "",
                message: "",
                npi: "",
                taxId: "",
                termsAccepted: false,
                interest: "join",
            });
            setSignature("");
            clearSignature();
        } catch (error) {
            setSubmitStatus("error");
            console.error("Error submitting form:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section
            className="contact-form"
            id="contact">
            <div className="container">
                <motion.div
                    className="contact-header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}>
                    <h2>Join Stamp Medical Today</h2>
                    <p>We're thrilled to get you on board! Once you fill out your information below we'll email you a welcome kit with next steps and resources to get started.</p>
                </motion.div>

                <motion.div
                    className="contact-content"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    viewport={{ once: true }}>
                    {submitStatus !== "success" && (
                        <form
                            className="form"
                            onSubmit={handleSubmit}>
                            {currentStep === 1 && (
                                <>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="name">Full Name *</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your full name"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="email">Email Address *</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="phone">Phone Number *</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your phone number"
                                                maxLength="14"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="specialty">Medical Specialty *</label>
                                            <select
                                                id="specialty"
                                                name="specialty"
                                                value={formData.specialty}
                                                onChange={handleChange}
                                                className={!formData.specialty ? 'placeholder' : ''}
                                                required>
                                                <option value="">Select your specialty</option>
                                                <option value="chiropractor">Chiropractor</option>
                                                <option value="physical-therapy">Physical Therapy</option>
                                                <option value="pain-management">Pain Management</option>
                                                <option value="orthopedics">Orthopedics</option>
                                                <option value="neurology">Neurology</option>
                                                <option value="imaging">Imaging</option>
                                                <option value="other">Other Medical Specialty</option>
                                            </select>
                                        </div>
                                    </div>

                                    {formData.specialty === 'chiropractor' && (
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label htmlFor="licenseNumber">License Number</label>
                                                <input
                                                    type="text"
                                                    id="licenseNumber"
                                                    name="licenseNumber"
                                                    value={formData.licenseNumber}
                                                    onChange={handleChange}
                                                    placeholder="Enter your chiropractic license number"
                                                />
                                            </div>
                                            <div className="form-group">
                                                {/* Empty div for spacing */}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="practiceName">Practice Name</label>
                                            <input
                                                type="text"
                                                id="practiceName"
                                                name="practiceName"
                                                value={formData.practiceName}
                                                onChange={handleChange}
                                                placeholder="Enter your practice name"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="dbaName">DBA Name</label>
                                            <input
                                                type="text"
                                                id="dbaName"
                                                name="dbaName"
                                                value={formData.dbaName}
                                                onChange={handleChange}
                                                placeholder="Enter your DBA name (if applicable)"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="location">Practice State *</label>
                                            <select
                                                id="location"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleChange}
                                                className={!formData.location ? 'placeholder' : ''}
                                                required>
                                                <option value="">Select your state</option>
                                                <option value="arizona">Arizona</option>
                                                <option
                                                    value="colorado"
                                                    disabled>
                                                    Colorado
                                                </option>
                                                <option value="nevada">Nevada</option>
                                                <option
                                                    value="new-mexico"
                                                    >
                                                    New Mexico
                                                </option>
                                                <option value="texas">Texas</option>
                                                <option
                                                    value="washington"
                                                    >
                                                    Washington
                                                </option>
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="referralEmail">Referral Email</label>
                                            <input
                                                type="email"
                                                id="referralEmail"
                                                name="referralEmail"
                                                value={formData.referralEmail}
                                                onChange={handleChange}
                                                placeholder="Enter your referral email"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="billingContact">Record/Billings Contact</label>
                                            <input
                                                type="text"
                                                id="billingContact"
                                                name="billingContact"
                                                value={formData.billingContact}
                                                onChange={handleChange}
                                                placeholder="Enter billing contact name"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="billingEmail">Record/Billings Email</label>
                                            <input
                                                type="email"
                                                id="billingEmail"
                                                name="billingEmail"
                                                value={formData.billingEmail}
                                                onChange={handleChange}
                                                placeholder="Enter billing email address"
                                            />
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleNextStep}>
                                        Continue
                                    </button>
                                </>
                            )}

                            {currentStep === 2 && (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="address">Practice Address *</label>
                                        <input
                                            type="text"
                                            id="address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            required
                                            placeholder="Enter your practice address"
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="city">City *</label>
                                            <input
                                                type="text"
                                                id="city"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your city"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="zip">ZIP Code *</label>
                                            <input
                                                type="text"
                                                id="zip"
                                                name="zip"
                                                value={formData.zip}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your ZIP code"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="crossStreets">What are your main cross streets? *</label>
                                            <input
                                                type="text"
                                                id="crossStreets"
                                                name="crossStreets"
                                                value={formData.crossStreets}
                                                onChange={handleChange}
                                                required
                                                placeholder="e.g., Main St & 1st Ave"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="practicePhone">Practice Phone Number *</label>
                                            <input
                                                type="tel"
                                                id="practicePhone"
                                                name="practicePhone"
                                                value={formData.practicePhone}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter practice phone number"
                                                maxLength="14"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="practiceFax">Practice Fax Number</label>
                                            <input
                                                type="tel"
                                                id="practiceFax"
                                                name="practiceFax"
                                                value={formData.practiceFax}
                                                onChange={handleChange}
                                                placeholder="Enter practice fax number"
                                                maxLength="14"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="availableHours">What are your available hours? *</label>
                                            <input
                                                type="text"
                                                id="availableHours"
                                                name="availableHours"
                                                value={formData.availableHours}
                                                onChange={handleChange}
                                                required
                                                placeholder="e.g., Mon-Fri 8AM-6PM, Sat 9AM-2PM"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="urgentAppointments">Are you flexible for urgent appointments? *</label>
                                        <select
                                            id="urgentAppointments"
                                            name="urgentAppointments"
                                            value={formData.urgentAppointments}
                                            onChange={handleChange}
                                            className={!formData.urgentAppointments ? 'placeholder' : ''}
                                            required>
                                            <option value="">Select an option</option>
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="message">Tell us about your practice *</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows="5"
                                            placeholder="Tell us about your practice, experience with Personal Injury patients, and any questions you have..."
                                        />
                                    </div>

                                    <div style={{ display: "flex", gap: "1rem", justifyContent: "center", marginTop: "2rem" }}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handlePrevStep}>
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleNextStep}>
                                            Continue
                                        </button>
                                    </div>
                                </>
                            )}

                            {currentStep === 3 && (
                                <>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="npi">NPI Number *</label>
                                            <input
                                                type="text"
                                                id="npi"
                                                name="npi"
                                                value={formData.npi}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your 10-digit NPI number"
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label htmlFor="taxId">Tax ID Number *</label>
                                            <input
                                                type="text"
                                                id="taxId"
                                                name="taxId"
                                                value={formData.taxId}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your Tax ID number"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <div className="checkbox-group">
                                            <input
                                                type="checkbox"
                                                id="termsAccepted"
                                                name="termsAccepted"
                                                checked={formData.termsAccepted}
                                                onChange={handleChange}
                                                required
                                            />
                                            <label htmlFor="termsAccepted">
                                                *I agree to{" "}
                                                <button
                                                    type="button"
                                                    className="terms-link"
                                                    onClick={openTermsModal}>
                                                    Terms & Conditions
                                                </button>{" "}
                                                &amp; understand this is a contractor agreement
                                            </label>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="signature">*Digital Signature</label>
                                        <div className="signature-container">
                                            <canvas
                                                ref={canvasRef}
                                                width={400}
                                                height={150}
                                                className="signature-canvas"
                                                onMouseDown={startDrawing}
                                                onMouseMove={draw}
                                                onMouseUp={stopDrawing}
                                                onMouseLeave={stopDrawing}
                                                onTouchStart={(e) => {
                                                    e.preventDefault();
                                                    const touch = e.touches[0];
                                                    const mouseEvent = new MouseEvent("mousedown", {
                                                        clientX: touch.clientX,
                                                        clientY: touch.clientY,
                                                    });
                                                    startDrawing(mouseEvent);
                                                }}
                                                onTouchMove={(e) => {
                                                    e.preventDefault();
                                                    const touch = e.touches[0];
                                                    const mouseEvent = new MouseEvent("mousemove", {
                                                        clientX: touch.clientX,
                                                        clientY: touch.clientY,
                                                    });
                                                    draw(mouseEvent);
                                                }}
                                                onTouchEnd={(e) => {
                                                    e.preventDefault();
                                                    stopDrawing();
                                                }}
                                            />
                                            <div className="signature-controls">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btn-small"
                                                    onClick={clearSignature}>
                                                    Clear Signature
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", gap: "1rem", justifyContent: "center", marginTop: "2rem" }}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handlePrevStep}>
                                            Back
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={isSubmitting || !formData.termsAccepted || !signature}>
                                            {isSubmitting ? "Submitting..." : "Join Stamp Medical"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    )}

                    {submitStatus !== "success" && (
                        <div className="contact-info">
                            <div className="contact-item">
                                <div className="contact-icon">
                                    <FontAwesomeIcon icon={faPhone} />
                                </div>
                                <div>
                                    <h3>Phone</h3>
                                    <p>Call us 623-773-2000</p>
                                </div>
                            </div>

                            <div className="contact-item">
                                <div className="contact-icon">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                </div>
                                <div>
                                    <h3>Email</h3>
                                    <p>info@stampmedical.com</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {submitStatus === "success" && (
                        <motion.div
                            className="form-message success"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}>
                            ✅ Great, we've got your information! Make sure to check you email for your welcome kit.
                        </motion.div>
                    )}

                    {submitStatus === "error" && (
                        <motion.div
                            className="form-message error"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}>
                            ❌ Something went wrong. Please try again.
                        </motion.div>
                    )}
                </motion.div>
            </div>

            {/* Terms & Conditions Modal */}
            {showTermsModal && (
                <div
                    className="modal-overlay"
                    onClick={closeTermsModal}>
                    <div
                        className="modal-content"
                        onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{getTermsContent().title}</h3>
                            <button
                                className="modal-close"
                                onClick={closeTermsModal}>
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="terms-content">
                                <iframe
                                    id="termsIframe"
                                    src="/agreements/joinstampterms.html"
                                    width="100%"
                                    style={{ border: "none", height: "calc(70vh - 40px)" }}
                                    title="Stamp Medical Clinical Contractor Agreement"
                                    onLoad={(e) => {
                                        const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;

                                        // Fill in contractor signature
                                        const sigField = iframeDoc.getElementById('contractor-signature');
                                        if (sigField && signature) {
                                            sigField.innerHTML = `By: <img src="${signature}" alt="Signature" style="height: 40px; vertical-align: middle; margin-left: 10px;" />`;
                                        }

                                        // Fill in contractor name
                                        const nameField = iframeDoc.getElementById('contractor-name');
                                        if (nameField && formData.name) {
                                            nameField.innerHTML = `Name: <span style="text-decoration: underline; margin-left: 5px;">${formData.name}</span>`;
                                        }

                                        // Fill in license type / state
                                        const licenseField = iframeDoc.getElementById('contractor-license');
                                        if (licenseField && (formData.specialty || formData.location)) {
                                            const specialty = formatSpecialty(formData.specialty);
                                            const state = formatState(formData.location);
                                            licenseField.innerHTML = `License Type / State(s): <span style="text-decoration: underline; margin-left: 5px;">${specialty} / ${state}</span>`;
                                        }

                                        // Fill in date
                                        const dateField = iframeDoc.getElementById('contractor-date');
                                        if (dateField) {
                                            dateField.innerHTML = `Date: <span style="text-decoration: underline; margin-left: 5px;">${new Date().toLocaleDateString()}</span>`;
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                style={{ marginRight: "10px" }}
                                onClick={() => {
                                    const iframe = document.getElementById('termsIframe');
                                    if (iframe && iframe.contentWindow) {
                                        iframe.contentWindow.print();
                                    }
                                }}>
                                🖨️ Print
                            </button>
                            <button
                                className="btn btn-secondary"
                                style={{ marginRight: "10px" }}
                                onClick={openFilledAgreementForPrint}>
                                ⬇️ Save as PDF
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={closeTermsModal}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ContactForm;
